import { useEffect, useMemo, useState } from "react";
import { Chart as GoogleChart } from "react-google-charts";
import {
  TrendingUp, TrendingDown, Wallet, Percent, RefreshCw, Loader2, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { groupByCategory, EXPENSE_DRILL_KINDS, CATEGORY_LABELS } from "../../lib/expenseGrouping";

/**
 * AdminFinancialCharts — dashboard financeiro com gráficos 3D de pizza.
 * Lê /api/admin/finance/charts?period=7d|30d|12m e renderiza 4 pies 3D:
 *   1. Receitas — distribuição por bucket temporal
 *   2. Despesas — distribuição por bucket temporal
 *   3. Composição financeira — Receitas vs Despesas vs Taxa Stripe vs Lucro
 *   4. Taxas Stripe — distribuição por bucket temporal
 */
const PERIODS = [
  { key: "7d", label: "7 dias" },
  { key: "30d", label: "30 dias" },
  { key: "12m", label: "12 meses" },
];

const GOLD = "#d4af37";
const GREEN = "#10b981";
const RED = "#ef4444";
const AMBER = "#f59e0b";

// Palette presets — one per metric so each pie reads distinctly on the
// dark theme. 12 entries cycle for 12-month views.
const PALETTE_GOLD = [
  "#d4af37", "#b8941e", "#e8c860", "#a07d12", "#f0d978",
  "#c89f2a", "#9b7c1c", "#dcb849", "#7d6310", "#f4e092",
  "#bb9520", "#d6b53a",
];
const PALETTE_RED = [
  "#ef4444", "#b91c1c", "#fca5a5", "#7f1d1d", "#f87171",
  "#dc2626", "#991b1b", "#fee2e2", "#fecaca", "#dc2626",
  "#b91c1c", "#7f1d1d",
];
const PALETTE_AMBER = [
  "#f59e0b", "#b45309", "#fbbf24", "#78350f", "#fcd34d",
  "#d97706", "#92400e", "#fde68a", "#fef3c7", "#fbbf24",
  "#d97706", "#b45309",
];

export default function AdminFinancialCharts() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { kind, title, items, loading }

  async function openDrill(kind, fallbackTitle) {
    setDrill({ kind, title: fallbackTitle, items: [], loading: true });
    try {
      const { data } = await api.get("/admin/finance/drill",
                                     { params: { kind, months: 12 } });
      setDrill({
        kind,
        title: data.title || fallbackTitle,
        items: data.items || [],
        loading: false,
        // Reload-self handler — called after a line edit so the drill
        // refreshes its numbers without closing.
        onReload: () => openDrill(kind, fallbackTitle),
      });
      // Refresh the underlying chart totals too, so the KPI cards align
      // with the (potentially mutated) line-level values.
      load(period);
    } catch (e) {
      toast.error(formatApiError(e));
      setDrill(null);
    }
  }

  async function load(p) {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/finance/charts`, { params: { period: p } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    load(period);
    const id = setInterval(() => load(period), 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const series = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map(r => ({
      ...r,
      pretty: data.granularity === "month"
        ? formatMonth(r.label)
        : formatDay(r.label),
    }));
  }, [data]);

  const totals = data?.totals || { revenue: 0, expenses: 0, despesas: 0, produtos: 0, materiais: 0, stripe_fees: 0, profit: 0 };
  const lifetime = data?.lifetime_totals || { revenue: 0, expenses: 0, despesas: 0, produtos: 0, materiais: 0, stripe_fees: 0, profit: 0 };
  const upcoming = data?.upcoming || { total: 0, count: 0, in_period_total: 0, in_period_count: 0 };
  const fee_pct = data?.fee_rate_percent ?? 2.23;

  return (
    <div className="space-y-8" data-testid="admin-financial-charts">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-[#d4af37] flex items-center gap-2">
            <TrendingUp size={14} /> Admin · Finanças
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Gráficos financeiros</h1>
          <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
            Totais acumulados desde o início. Os gráficos 3D abaixo mostram
            a distribuição no período selecionado (7d / 30d / 12m). Passe o
            rato sobre cada fatia para ver valores detalhados.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-sm border border-[#1a4640] overflow-hidden" data-testid="period-filter">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                  period === p.key
                    ? "bg-[#d4af37] text-[#041f1b] font-semibold"
                    : "text-[#a0b2af] hover:text-[#d4af37]"
                }`}
                data-testid={`period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => load(period)}
            disabled={loading}
            className="p-2 border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] disabled:opacity-50"
            title="Atualizar agora"
            data-testid="finance-refresh"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>
      </header>

      {/* SALDO ATUAL — Resultado Operacional consolidado.
          Receitas − (Produtos + Materiais + Despesas operacionais + Taxas Stripe).
          Verde quando positivo · Vermelho quando negativo. */}
      <OperationalResultCard
        lifetime={lifetime}
        totals={totals}
        period={period}
        feePct={fee_pct}
      />

      {/* KPIs acumulados (lifetime) */}
      <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="finance-kpis">
        <Kpi label="Receitas (acumulado)"
             value={`${lifetime.revenue.toFixed(2)} €`}
             icon={<TrendingUp size={14} />} color={GREEN}
             sub={`No período: ${totals.revenue.toFixed(2)} €`}
             onClick={() => openDrill("revenue", "Receitas")}
             testId="kpi-revenue" />
        <Kpi label="Despesas operacionais"
             value={`− ${(lifetime.despesas || 0).toFixed(2)} €`}
             icon={<TrendingDown size={14} />} color={RED}
             sub={`No período: ${(totals.despesas || 0).toFixed(2)} €`}
             onClick={() => openDrill("despesas", "Despesas operacionais")}
             testId="kpi-despesas" />
        <Kpi label="Compra de produtos"
             value={`− ${(lifetime.produtos || 0).toFixed(2)} €`}
             icon={<TrendingDown size={14} />} color={RED}
             sub={`No período: ${(totals.produtos || 0).toFixed(2)} €`}
             onClick={() => openDrill("produtos", "Compras de produtos")}
             testId="kpi-produtos" />
        <Kpi label="Compra de materiais"
             value={`− ${(lifetime.materiais || 0).toFixed(2)} €`}
             icon={<TrendingDown size={14} />} color={RED}
             sub={`No período: ${(totals.materiais || 0).toFixed(2)} €`}
             onClick={() => openDrill("materiais", "Compras de materiais")}
             testId="kpi-materiais" />
        <Kpi label={`Taxa Stripe (${fee_pct.toFixed(2)}%)`}
             value={`− ${lifetime.stripe_fees.toFixed(2)} €`}
             icon={<Percent size={14} />} color={AMBER}
             sub={`No período: ${totals.stripe_fees.toFixed(2)} €`}
             onClick={() => openDrill("stripe_fees", "Taxas Stripe")}
             testId="kpi-stripe" />
        <Kpi label="Lucro líquido (acumulado)"
             value={`${lifetime.profit.toFixed(2)} €`}
             icon={<Wallet size={14} />}
             color={lifetime.profit >= 0 ? GREEN : RED}
             highlight
             sub={`No período: ${totals.profit.toFixed(2)} €`}
             onClick={() => openDrill("profit", "Lucro líquido")}
             testId="kpi-profit" />
      </section>

      {/* Pagamentos por vir — reservas registadas mas ainda não pagas.
          Clicável: abre o drill-down com split sem/com IVA. */}
      <UpcomingPaymentsCard
        upcoming={upcoming}
        onOpen={() => openDrill("upcoming", "Pagamentos por vir")}
      />

      {/* IVA facturado por mês — bar chart dos últimos 12 meses */}
      <VatMonthlyChart />

      {/* Receitas líquidas de compras — highlight card */}
      <NetAfterPurchasesCard totals={totals} lifetime={lifetime} period={period} />

      {/* Pie 1 — Revenue distribution */}
      <Pie3D
        title="Receitas"
        subtitle={`Distribuição por ${data?.granularity === "month" ? "mês" : "dia"} · ${periodLabel(period)}`}
        testId="chart-revenue"
        rows={series}
        valueKey="revenue"
        emptyMsg="Sem receitas no período seleccionado."
        palette={PALETTE_GOLD}
      />

      {/* Pie 2 — Expense breakdown by category (Despesas / Produtos / Materiais) */}
      <Pie3D
        title="Custos por categoria"
        subtitle={`Despesas operacionais, compras de produtos e compras de materiais · ${periodLabel(period)}`}
        testId="chart-expense-breakdown"
        compositionRows={[
          ["Categoria", "Valor (€)"],
          ["Despesas operacionais", Math.max(0, totals.despesas || 0)],
          ["Produtos", Math.max(0, totals.produtos || 0)],
          ["Materiais", Math.max(0, totals.materiais || 0)],
        ]}
        emptyMsg="Sem custos no período seleccionado."
        palette={[RED, "#f97316", "#ef4444"]}
      />

      {/* Pie 3 — Despesas operacionais distribution over time */}
      <Pie3D
        title="Despesas operacionais"
        subtitle={`Distribuição por ${data?.granularity === "month" ? "mês" : "dia"} · ${periodLabel(period)}`}
        testId="chart-expenses"
        rows={series}
        valueKey="despesas"
        emptyMsg="Sem despesas operacionais no período seleccionado."
        palette={PALETTE_RED}
      />

      {/* Pie 4 — Produtos distribution over time */}
      <Pie3D
        title="Compra de produtos"
        subtitle={`Distribuição por ${data?.granularity === "month" ? "mês" : "dia"} · ${periodLabel(period)}`}
        testId="chart-produtos"
        rows={series}
        valueKey="produtos"
        emptyMsg="Sem compras de produtos no período seleccionado."
        palette={PALETTE_RED}
      />

      {/* Pie 5 — Materiais distribution over time */}
      <Pie3D
        title="Compra de materiais"
        subtitle={`Distribuição por ${data?.granularity === "month" ? "mês" : "dia"} · ${periodLabel(period)}`}
        testId="chart-materiais"
        rows={series}
        valueKey="materiais"
        emptyMsg="Sem compras de materiais no período seleccionado."
        palette={PALETTE_RED}
      />

      {/* Pie 6 — Overall composition (revenue vs 3 expense buckets vs fees vs profit) */}
      <Pie3D
        title="Composição financeira"
        subtitle={`Receitas, despesas, produtos, materiais, taxas Stripe e lucro · ${periodLabel(period)}`}
        testId="chart-profit"
        compositionRows={[
          ["Categoria", "Valor (€)"],
          ["Receitas", Math.max(0, totals.revenue)],
          ["Despesas operacionais", Math.max(0, totals.despesas || 0)],
          ["Produtos", Math.max(0, totals.produtos || 0)],
          ["Materiais", Math.max(0, totals.materiais || 0)],
          [`Taxa Stripe ${fee_pct.toFixed(2)}%`, Math.max(0, totals.stripe_fees)],
          ["Lucro líquido", Math.max(0, totals.profit)],
        ]}
        emptyMsg="Sem movimentos no período seleccionado."
        palette={[GREEN, RED, "#f97316", "#ef4444", AMBER, GOLD]}
      />

      {/* Pie 4 — Stripe fees distribution */}
      <Pie3D
        title={`Taxas Stripe (${fee_pct.toFixed(2)}%)`}
        subtitle={`Distribuição por ${data?.granularity === "month" ? "mês" : "dia"} · ${periodLabel(period)}`}
        testId="chart-stripe-fees"
        rows={series}
        valueKey="stripe_fees"
        emptyMsg="Sem pagamentos Stripe no período seleccionado."
        palette={PALETTE_AMBER}
      />

      {drill && <KpiDetailModal drill={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

/* ---------- Sub-components ---------- */

function NetAfterPurchasesCard({ totals, lifetime, period }) {
  const periodLabelStr = periodLabel(period);
  const periodNet = (Number(totals.revenue) || 0)
                    - (Number(totals.produtos) || 0)
                    - (Number(totals.materiais) || 0);
  const lifetimeNet = (Number(lifetime.revenue) || 0)
                      - (Number(lifetime.produtos) || 0)
                      - (Number(lifetime.materiais) || 0);
  const positive = lifetimeNet >= 0;
  const valueTone = positive ? "text-[#10b981]" : "text-[#fca5a5]";
  const borderTone = positive
    ? "border-[#10b981]/40 bg-[#10b981]/5"
    : "border-[#ef4444]/50 bg-[#ef4444]/10";

  return (
    <section className={`lux-card p-6 ${borderTone} transition-colors`}
             data-testid="finance-net-after-purchases">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow text-[#a0b2af] flex items-center gap-2">
            {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            Receitas − Produtos − Materiais (acumulado)
          </div>
          <div className={`font-display text-4xl sm:text-5xl mt-2 tabular-nums ${valueTone}`}
               data-testid="finance-net-lifetime">
            {positive ? "" : "− "}
            {Math.abs(lifetimeNet).toFixed(2)} €
          </div>
          <div className="text-xs text-[#a0b2af] mt-2 max-w-xl">
            Total das receitas menos as compras de produtos e de materiais,
            desde o início. Valor positivo significa que as receitas cobrem
            todo o stock comprado; negativo significa que gastou mais em
            stock do que faturou.
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
            No período ({periodLabelStr})
          </div>
          <div className={`font-display text-2xl mt-1 tabular-nums ${periodNet >= 0 ? "text-[#10b981]" : "text-[#fca5a5]"}`}
               data-testid="finance-net-period">
            {periodNet >= 0 ? "" : "− "}
            {Math.abs(periodNet).toFixed(2)} €
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-[#1a4640]">
        <MiniBar label="Receitas" value={lifetime.revenue} sign="+" tone="text-[#10b981]" />
        <MiniBar label="Produtos" value={lifetime.produtos || 0} sign="−" tone="text-[#fca5a5]" />
        <MiniBar label="Materiais" value={lifetime.materiais || 0} sign="−" tone="text-[#fca5a5]" />
      </div>
    </section>
  );
}

function MiniBar({ label, value, sign, tone }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className={`font-display text-lg mt-0.5 tabular-nums ${tone}`}>
        {sign} {Number(value || 0).toFixed(2)} €
      </div>
    </div>
  );
}

function Kpi({ label, value, icon, color, highlight, sub, testId, onClick }) {
  const clickable = typeof onClick === "function";
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      onClick={onClick}
      className={`lux-card p-4 text-left w-full ${highlight ? "ring-1 ring-[#d4af37]/30" : ""}
                  ${clickable ? "hover:border-[#d4af37] hover:shadow-[0_0_0_1px_rgba(212,175,55,0.4)] cursor-pointer transition-all" : ""}`}
      data-testid={testId}>
      <div className="eyebrow text-[10px] flex items-center gap-1.5 text-[#a0b2af]">
        {icon} {label}
        {clickable && <span className="ml-auto text-[#d4af37] text-[9px]">ver detalhes ›</span>}
      </div>
      <div className="font-display text-3xl mt-2" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-[#a0b2af] mt-1.5">{sub}</div>}
    </Wrapper>
  );
}

/**
 * 3D pie chart card. Either:
 *   - bucket mode: pass `rows` (time-bucket series) + `valueKey`
 *   - composition mode: pass `compositionRows` (already in google-charts format)
 *
 * Click any slice to "explode" it outwards. Click again to collapse.
 */
function Pie3D({ title, subtitle, testId, rows, valueKey, compositionRows, emptyMsg, palette }) {
  const [explodedIdx, setExplodedIdx] = useState(null);

  const chartData = useMemo(() => {
    if (compositionRows) {
      const header = compositionRows[0];
      const body = compositionRows.slice(1).filter(r => Number(r[1]) > 0);
      return [header, ...body];
    }
    const header = ["Período", "Valor (€)"];
    const body = (rows || [])
      .map(r => [r.pretty || r.label, Number(r[valueKey] || 0)])
      .filter(r => r[1] > 0);
    return [header, ...body];
  }, [rows, valueKey, compositionRows]);

  // Reset explode index whenever the dataset changes
  useEffect(() => { setExplodedIdx(null); }, [chartData.length, testId]);

  const hasData = chartData.length > 1;
  const total = chartData.slice(1).reduce((s, r) => s + Number(r[1] || 0), 0);

  // Build the dynamic `slices` object so the selected slice "explodes".
  const slicesOption = useMemo(() => {
    if (explodedIdx == null) return undefined;
    return { [explodedIdx]: { offset: 0.2 } };
  }, [explodedIdx]);

  return (
    <section className="lux-card p-6" data-testid={testId}>
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="font-display text-xl text-[#fdfbf7]">{title}</div>
          {subtitle && <div className="text-xs text-[#a0b2af] mt-0.5">{subtitle}</div>}
          <div className="text-[10px] text-[#a0b2af] mt-1 italic">
            Clique numa fatia para a destacar.
          </div>
        </div>
        {hasData && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Total no período</div>
            <div className="font-display text-lg text-[#d4af37]">{total.toFixed(2)} €</div>
          </div>
        )}
      </div>
      {hasData ? (
        <GoogleChart
          chartType="PieChart"
          data={chartData}
          width="100%"
          height="340px"
          options={{
            is3D: true,
            backgroundColor: "transparent",
            legend: { textStyle: { color: "#a0b2af", fontSize: 11 } },
            pieSliceTextStyle: { color: "#041f1b", fontSize: 11, bold: true },
            pieSliceText: "percentage",
            tooltip: {
              showColorCode: true,
              textStyle: { color: "#041f1b", fontSize: 12 },
            },
            chartArea: { width: "92%", height: "82%" },
            colors: palette,
            sliceVisibilityThreshold: 0,
            slices: slicesOption,
          }}
          chartEvents={[
            {
              eventName: "select",
              callback: ({ chartWrapper }) => {
                const sel = chartWrapper.getChart().getSelection();
                if (!sel || sel.length === 0) {
                  setExplodedIdx(null);
                  return;
                }
                const idx = sel[0].row;
                // Toggle: clicking the same slice collapses it
                setExplodedIdx((prev) => (prev === idx ? null : idx));
              },
            },
          ]}
        />
      ) : (
        <div className="h-[300px] flex items-center justify-center text-sm text-[#a0b2af]"
             data-testid={`${testId}-empty`}>
          {emptyMsg}
        </div>
      )}
    </section>
  );
}

/* ---------- helpers ---------- */
function formatDay(iso) {
  if (!iso || iso.length < 10) return iso;
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

const MONTH_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
                  "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function formatMonth(ym) {
  if (!ym || ym.length < 7) return ym;
  const m = parseInt(ym.slice(5, 7), 10);
  const y = ym.slice(2, 4);
  return `${MONTH_PT[m - 1] || ""}/${y}`;
}

function periodLabel(p) {
  if (p === "7d") return "últimos 7 dias";
  if (p === "30d") return "últimos 30 dias";
  return "últimos 12 meses";
}


/**
 * OperationalResultCard — KPI principal e mais visível do dashboard.
 *
 * Mostra o «Resultado Operacional» consolidado:
 *   Saldo = Receitas − (Produtos + Materiais + Despesas Operacionais + Taxas Stripe)
 *
 * Verde quando positivo · Vermelho quando negativo. Atualiza em tempo real
 * sempre que `lifetime` muda (o componente pai recarrega a cada minuto).
 */
function OperationalResultCard({ lifetime, totals, period, feePct }) {
  const saldo = Number(lifetime.profit || 0);
  const positive = saldo >= 0;
  const periodSaldo = Number(totals.profit || 0);
  const periodPositive = periodSaldo >= 0;
  const tone = positive
    ? { border: "border-[#10b981]", glow: "shadow-[0_0_60px_-10px_rgba(16,185,129,0.45)]",
        bg: "bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.12),transparent_60%)]",
        text: "text-[#10b981]", label: "Saldo positivo" }
    : { border: "border-[#ef4444]", glow: "shadow-[0_0_60px_-10px_rgba(239,68,68,0.45)]",
        bg: "bg-[radial-gradient(circle_at_top_left,rgba(239,68,68,0.12),transparent_60%)]",
        text: "text-[#ef4444]", label: "Saldo negativo · operação em prejuízo" };

  const periodLbl = period === "7d" ? "últimos 7 dias"
                  : period === "30d" ? "últimos 30 dias"
                  : "últimos 12 meses";

  return (
    <section
      className={`relative overflow-hidden border-2 ${tone.border} ${tone.glow} rounded-md p-5 sm:p-6 lg:p-8 ${tone.bg}`}
      data-testid="operational-result-card"
    >
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 lg:gap-6 items-stretch">
        {/* LEFT — the big consolidated balance number */}
        <div className="space-y-3 min-w-0">
          <div className="flex items-center gap-2">
            <Wallet size={14} className={tone.text} />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0b2af]">
              Saldo Atual · Resultado Operacional
            </div>
          </div>
          <div className={`font-display text-4xl sm:text-5xl lg:text-7xl ${tone.text} leading-none break-words`}
               data-testid="operational-result-value">
            {positive ? "" : "− "}
            {Math.abs(saldo).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </div>
          <div className={`text-xs uppercase tracking-widest ${tone.text}`}
               data-testid="operational-result-label">
            {tone.label}
          </div>
          <div className="text-[11px] text-[#a0b2af] leading-relaxed max-w-md mt-3">
            Receitas <span className="text-[#fdfbf7]">−</span> Produtos
            <span className="text-[#fdfbf7]"> −</span> Materiais
            <span className="text-[#fdfbf7]"> −</span> Despesas operacionais
            <span className="text-[#fdfbf7]"> −</span> Taxa Stripe ({feePct.toFixed(2)}%).
            <span className="block mt-1">Acumulado desde o início da operação · <strong className="text-[#fdfbf7]">apenas reservas pagas</strong>.</span>
          </div>
        </div>

        {/* RIGHT — period subtotal + breakdown line. On mobile, top border
            instead of left border so it stacks naturally. */}
        <div className="flex flex-col justify-between gap-4 border-t lg:border-t-0 lg:border-l border-[#1a4640] pt-5 lg:pt-0 lg:pl-6 min-w-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0b2af] mb-1">
              No período ({periodLbl})
            </div>
            <div className={`font-display text-3xl ${periodPositive ? "text-[#10b981]" : "text-[#ef4444]"} break-words`}
                 data-testid="operational-result-period">
              {periodPositive ? "" : "− "}
              {Math.abs(periodSaldo).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
          </div>
          <div className="space-y-1.5 text-[11px]">
            <ResultRow label="Receitas" value={lifetime.revenue || 0} sign="+" tone="text-[#10b981]" />
            <ResultRow label="Produtos" value={lifetime.produtos || 0} sign="−" tone="text-[#fca5a5]" />
            <ResultRow label="Materiais" value={lifetime.materiais || 0} sign="−" tone="text-[#fca5a5]" />
            <ResultRow label="Despesas operacionais" value={lifetime.despesas || 0} sign="−" tone="text-[#fca5a5]" />
            <ResultRow label={`Taxa Stripe (${feePct.toFixed(2)}%)`} value={lifetime.stripe_fees || 0} sign="−" tone="text-[#fcd34d]" />
            <div className="border-t border-[#1a4640] mt-2 pt-2 flex items-center justify-between">
              <span className={`uppercase tracking-widest ${tone.text}`}>= Saldo</span>
              <span className={`font-medium ${tone.text}`}>
                {positive ? "" : "− "}
                {Math.abs(saldo).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultRow({ label, value, sign, tone }) {
  return (
    <div className="flex items-center justify-between text-[#a0b2af]">
      <span>{label}</span>
      <span className={tone}>
        {sign} {Number(value).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
      </span>
    </div>
  );
}


/**
 * VatMonthlyChart — bar chart com o IVA facturado mês a mês.
 *
 * Lê /api/admin/finance/vat-monthly e desenha 3 séries por mês:
 *   • Sem IVA (líquido) — receita real
 *   • IVA facturado — valor que a LuxStay vai entregar à AT
 *   • Com IVA (total) — bruto
 *
 * Importante p/ declaração trimestral de IVA.
 */
function VatMonthlyChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/finance/vat-monthly", { params: { months } });
        if (alive) setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [months]);

  const chartData = useMemo(() => {
    const header = ["Mês", "Sem IVA", "IVA facturado"];
    const rows = (data?.months || []).map((m) => [m.month, Number(m.net || 0), Number(m.vat || 0)]);
    return [header, ...rows];
  }, [data]);

  const hasData = chartData.length > 1 && chartData.slice(1).some((r) => (r[1] + r[2]) > 0);
  const fmtEur = (v) => `${Number(v || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €`;

  return (
    <section className="lux-card p-6" data-testid="vat-monthly-chart">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="font-display text-xl text-[#fdfbf7]">IVA facturado por mês</div>
          <div className="text-xs text-[#a0b2af] mt-0.5">
            Só facturas manuais. Limpezas (IVA isento art.º 53.º) não entram neste gráfico.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {[6, 12, 24].map((n) => (
            <button
              key={n}
              onClick={() => setMonths(n)}
              className={`text-[11px] uppercase tracking-wider px-3 py-1.5 border transition ${
                months === n
                  ? "bg-[#d4af37] text-[#010c0a] border-[#d4af37]"
                  : "border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37]/50 hover:text-[#fdfbf7]"
              }`}
              data-testid={`vat-monthly-range-${n}`}
            >
              {n} meses
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-72 flex items-center justify-center text-sm text-[#a0b2af]">A carregar…</div>
      ) : !hasData ? (
        <div className="h-72 flex items-center justify-center text-center text-sm text-[#a0b2af]">
          Sem IVA facturado nos últimos {months} meses.
        </div>
      ) : (
        <>
          <div data-testid="vat-monthly-chart-canvas">
            <GoogleChart
              chartType="ColumnChart"
              width="100%"
              height="320px"
              data={chartData}
              options={{
                isStacked: true,
                backgroundColor: "transparent",
                chartArea: { left: 60, right: 20, top: 20, bottom: 60, width: "95%", height: "75%" },
                colors: ["#d4af37", "#fca5a5"],
                legend: { position: "top", alignment: "end", textStyle: { color: "#a0b2af", fontSize: 11 } },
                hAxis: { textStyle: { color: "#a0b2af", fontSize: 11 }, slantedText: true, slantedTextAngle: 30 },
                vAxis: { textStyle: { color: "#a0b2af", fontSize: 11 }, format: "#,##0 €", gridlines: { color: "#1a4640" }, baselineColor: "#1a4640" },
                tooltip: { textStyle: { color: "#010c0a", fontSize: 12 } },
              }}
            />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-right"
               data-testid="vat-monthly-totals">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Sem IVA</div>
              <div className="font-display text-lg text-[#d4af37] tabular-nums">
                {fmtEur(data?.total_net)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">IVA facturado</div>
              <div className="font-display text-lg text-[#fca5a5] tabular-nums"
                   data-testid="vat-monthly-total-vat">
                {fmtEur(data?.total_vat)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Com IVA</div>
              <div className="font-display text-lg text-[#fcd34d] tabular-nums">
                {fmtEur(data?.total_invoiced)}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}


/**
 * UpcomingPaymentsCard — Pagamentos por vir.
 *
 * Mostra o total de reservas registadas mas ainda não pagas. Estas reservas
 * NÃO contam para receitas/saldo até o admin marcar como pagas — esta secção
 * existe para visualizar o pipeline de cash-in esperado.
 */
function UpcomingPaymentsCard({ upcoming, onOpen }) {
  const total = Number(upcoming?.total || 0);
  const count = Number(upcoming?.count || 0);
  const periodTotal = Number(upcoming?.in_period_total || 0);
  const periodCount = Number(upcoming?.in_period_count || 0);
  return (
    <section
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (!onOpen) return;
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); }
      }}
      className={`relative overflow-hidden border border-[#1a4640] rounded-md p-5 lg:p-6
                 bg-[radial-gradient(circle_at_top_right,rgba(245,158,11,0.08),transparent_55%)]
                 ${onOpen ? "cursor-pointer hover:border-[#d4af37] transition" : ""}`}
      data-testid="upcoming-payments-card"
    >
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 items-stretch">
        <div className="space-y-2 min-w-0">
          <div className="flex items-center gap-2">
            <Clock size={13} className="text-[#fcd34d]" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0b2af]">
              Pagamentos por vir · não contabilizado no saldo
            </div>
            {onOpen && (
              <span className="ml-auto text-[10px] uppercase tracking-wider text-[#d4af37]/80">
                Ver detalhes ›
              </span>
            )}
          </div>
          <div className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#fcd34d] leading-none break-words"
               data-testid="upcoming-payments-value">
            {total.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </div>
          <div className="text-[11px] text-[#a0b2af]" data-testid="upcoming-payments-count">
            {count} {count === 1 ? "reserva registada" : "reservas registadas"} ainda por pagar
          </div>
          <div className="text-[11px] text-[#a0b2af] max-w-md mt-2">
            Este valor representa a pipeline de cash-in esperado. Apenas conta para
            receitas / saldo quando confirmar o pagamento na ficha da reserva.
          </div>
        </div>
        <div className="flex flex-col justify-center gap-2 border-t lg:border-t-0 lg:border-l border-[#1a4640] pt-4 lg:pt-0 lg:pl-5 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0b2af]">
            Agendado para o período
          </div>
          <div className="font-display text-2xl text-[#fdfbf7]"
               data-testid="upcoming-payments-period-value">
            {periodTotal.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </div>
          <div className="text-[11px] text-[#a0b2af]">
            {periodCount} {periodCount === 1 ? "reserva" : "reservas"} com data dentro do período
          </div>
        </div>
      </div>
    </section>
  );
}


/**
 * Drill-down modal — shown when an admin clicks any KPI card. Lists the
 * individual line items that compose the KPI's number (e.g. each product
 * purchase, each finance_expense row…), with a total at the bottom.
 *
 * The list is virtualised lightly via `max-h` + overflow scroll because
 * production accounts may have hundreds of rows over 12 months.
 */
function KpiDetailModal({ drill, onClose }) {
  const { kind, title, items = [], loading, onReload } = drill;
  const total = items.reduce((s, it) => s + (Number(it.value) || 0), 0);

  // Group by category for expense-flavoured drill-downs. Non-expense kinds
  // (revenue, profit, stripe_fees) keep the original flat list.
  const isExpenseKind = EXPENSE_DRILL_KINDS.has(kind);
  // When kind is 'produtos'/'materiais' the payload's `sub` doesn't repeat
  // the category token — use the drill kind as fallback so items don't
  // land in the misleading 'Outro' bucket.
  const fallbackCat = (kind === "produtos" || kind === "materiais") ? kind : "other";
  const groups = isExpenseKind ? groupByCategory(items, fallbackCat) : null;
  const [openGroups, setOpenGroups] = useState({});

  const fmtEur = (v) => `${Math.abs(Number(v || 0)).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €`;

  async function editLine(it) {
    // Pequeno wizard via window.prompt — 3 perguntas: qty, preço, IVA.
    // Solução simples e funcional (sem mais um modal). O backend valida.
    const inv = it.invoice_id;
    const lid = it.line_id;
    if (!inv || !lid) return;
    const currentLabel = it.sub || "";
    const qty = window.prompt(`Quantidade nova para «${it.label}»\n(${currentLabel})\n\nDeixe vazio para não alterar.`);
    if (qty === null) return;
    const price = window.prompt("Preço unitário (€) novo?\n\nDeixe vazio para não alterar.");
    if (price === null) return;
    const vat = window.prompt("Taxa de IVA (%) nova?\n\nDeixe vazio para não alterar.");
    if (vat === null) return;
    const body = {};
    if (qty.trim()) body.quantity = parseFloat(qty.replace(",", "."));
    if (price.trim()) body.unit_price = parseFloat(price.replace(",", "."));
    if (vat.trim()) body.vat_rate = parseFloat(vat.replace(",", "."));
    if (Object.keys(body).length === 0) return;
    try {
      await api.patch(`/admin/stock/invoices/${inv}/lines/${lid}`, body);
      toast.success("Linha atualizada — totais a recarregar…");
      if (onReload) await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível atualizar a linha.");
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
         onClick={onClose} data-testid="kpi-drill-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="lux-card max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between gap-4 p-5 border-b border-[#1a4640]">
          <div>
            <div className="eyebrow text-[10px] text-[#d4af37]">Detalhe</div>
            <h2 className="font-display text-2xl mt-1">{title}</h2>
          </div>
          <button onClick={onClose}
                  className="p-2 hover:bg-[#1a4640] rounded-sm text-[#a0b2af] hover:text-[#fdfbf7]"
                  data-testid="kpi-drill-close">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {loading && (
            <div className="text-center text-sm text-[#a0b2af] py-10">A carregar…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="text-center text-sm text-[#a0b2af] py-10">Sem registos para este período.</div>
          )}
          {!loading && isExpenseKind && groups && groups.map((g) => {
            const isOpen = !!openGroups[g.category];
            return (
              <div key={g.category}
                   className="bg-[#062c27] border border-[#1a4640]"
                   data-testid={`kpi-drill-group-${g.category}`}>
                <button
                  onClick={() => setOpenGroups((p) => ({ ...p, [g.category]: !p[g.category] }))}
                  className="w-full text-left p-3 flex items-center justify-between hover:bg-[#0a3a33]/40"
                  data-testid={`kpi-drill-group-toggle-${g.category}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-[#a0b2af] text-xs w-4 shrink-0">{isOpen ? "▾" : "▸"}</span>
                    <div className="min-w-0">
                      <div className="text-sm text-[#fdfbf7] truncate">
                        {CATEGORY_LABELS[g.category] || g.category}
                      </div>
                      <div className="text-[11px] text-[#a0b2af] mt-0.5">
                        {g.items.length} {g.items.length === 1 ? "registo" : "registos"}
                      </div>
                    </div>
                  </div>
                  <div className="font-display text-lg tabular-nums whitespace-nowrap text-[#fca5a5]">
                    − {fmtEur(g.total)}
                  </div>
                </button>
                {isOpen && (
                  <div className="divide-y divide-[#1a4640]/60 bg-[#031815]"
                       data-testid={`kpi-drill-group-items-${g.category}`}>
                    {g.items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 px-4 py-2 pl-10">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-[#fdfbf7] truncate">{it.label || "—"}</div>
                          {it.sub && <div className="text-[11px] text-[#a0b2af] mt-0.5">{it.sub}</div>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="font-mono text-sm text-[#fca5a5]">
                            − {fmtEur(it.value)}
                          </div>
                          {it.kind === "stock_invoice_line" && it.invoice_id && it.line_id && (
                            <button
                              type="button"
                              onClick={() => editLine(it)}
                              className="text-[10px] uppercase tracking-wider text-[#d4af37] hover:text-[#fdfbf7] border border-[#d4af37]/40 hover:border-[#d4af37] rounded px-2 py-1 transition"
                              data-testid={`kpi-drill-edit-${g.category}-${i}`}
                              title="Corrigir quantidade / preço / IVA"
                            >
                              Editar
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {!loading && !isExpenseKind && kind === "upcoming" && items.map((it, i) => (
            <div key={i} className="flex items-center justify-between gap-3 p-3 bg-[#062c27] border border-[#1a4640]"
                 data-testid={`upcoming-item-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#fdfbf7] truncate">{it.label || "—"}</div>
                {it.sub && <div className="text-[11px] text-[#a0b2af] mt-0.5">{it.sub}</div>}
              </div>
              <div className="text-right shrink-0 tabular-nums">
                <div className="text-sm font-mono text-[#fcd34d]">
                  {fmtEur(it.value)}
                </div>
                {(it.vat || 0) > 0 && (
                  <div className="text-[10px] text-[#a0b2af] mt-0.5">
                    s/IVA <span className="text-[#d4af37]">{fmtEur(it.amount_net)}</span>
                    {" · "}IVA <span className="text-[#fca5a5]">{fmtEur(it.vat)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
          {!loading && !isExpenseKind && kind !== "upcoming" && items.map((it, i) => (
            <div key={i} className="flex items-center justify-between gap-3 p-3 bg-[#062c27] border border-[#1a4640]"
                 data-testid={`kpi-drill-item-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#fdfbf7] truncate">{it.label || "—"}</div>
                {it.sub && <div className="text-[11px] text-[#a0b2af] mt-0.5">{it.sub}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className={`font-mono text-sm ${it.negative ? "text-[#fca5a5]" : "text-[#d4af37]"}`}>
                  {it.value_display
                    ? it.value_display
                    : `${it.negative ? "− " : ""}${fmtEur(it.value)}`}
                </div>
              </div>
            </div>
          ))}
        </div>

        {!loading && items.length > 0 && kind === "upcoming" && (() => {
          const totNet = items.reduce((s, it) => s + Number(it.amount_net || it.value || 0), 0);
          const totVat = items.reduce((s, it) => s + Number(it.vat || 0), 0);
          const totGross = totNet + totVat;
          return (
            <div className="p-5 border-t border-[#1a4640] grid grid-cols-3 gap-3 text-right"
                 data-testid="upcoming-totals">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Sem IVA</div>
                <div className="font-display text-xl tabular-nums text-[#d4af37]"
                     data-testid="upcoming-total-net">
                  {fmtEur(totNet)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">IVA</div>
                <div className="font-display text-xl tabular-nums text-[#fca5a5]"
                     data-testid="upcoming-total-vat">
                  {fmtEur(totVat)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Com IVA</div>
                <div className="font-display text-xl tabular-nums text-[#fcd34d]"
                     data-testid="upcoming-total-gross">
                  {fmtEur(totGross)}
                </div>
              </div>
            </div>
          );
        })()}

        {!loading && items.length > 0 && kind !== "upcoming" && (
          <div className="p-5 border-t border-[#1a4640] flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-[#a0b2af]">
              Total · {items.length} registo(s)
            </div>
            <div className={`font-display text-xl tabular-nums ${isExpenseKind ? "text-[#fca5a5]" : "text-[#d4af37]"}`}
                 data-testid="kpi-drill-total">
              {isExpenseKind || total < 0 ? "− " : ""}{fmtEur(total)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
